import { Link } from "react-router-dom";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import ServiceSchema from "../ServiceSchema";
import HeroSection from "./HeroSection";          // Same folder
import industrialStructure from "../assets/industrial-structure.jpg"; // Adjust if needed

const IndustrialStructures = () => {
  const applications = [
    "Heavy Machinery Units",
    "Warehouses & Distribution Centers",
    "Automotive & Assembly Units",
    "Chemical/Mechanical Processing Units",
    "Fabrication & Welding Workstations",
    "Storage Facilities & Raw Material Yards",
  ];

  const features = [
    {
      title: "Strong Structural Framework",
      description: "Engineered for long-term durability and performance.",
    },
    {
      title: "Custom-Built to Operational Needs",
      description: "Designed according to your production flow, equipment, and machinery.",
    },
    {
      title: "Premium Steel Materials",
      description: "Ensuring resistance against corrosion, deformation, and wear.",
    },
    {
      title: "Flexible Expansion Options",
      description: "Structures that grow with your business needs.",
    },
    {
      title: "High Safety Standards",
      description: "Built with industrial safety regulations in mind.",
    },
  ];

  return (
    <Layout>
      <SEOHead
        title="Industrial Structures & Warehouse Buildings in Hyderabad | TR Structures"
        description="TR Structures specializes in industrial structures, warehouse buildings, and professional welding works near you. We deliver durable, custom steel solutions for all industrial needs in Hyderabad."
        keywords="industrial structures, warehouse building, welding works, welding works near me, industrial structures Hyderabad, warehouse buildings Hyderabad, steel warehouse construction, custom industrial structures, professional welding services"
      />
      <ServiceSchema
        serviceType="Industrial Structures & Warehouse Buildings"
        description="Design and construction of large-scale industrial structures and warehouse buildings for manufacturing, storage and distribution."
      />

      <main>
        <HeroSection
          title="Industrial Structures"
          subtitle="Large-Scale Steel Structures"
          description="Robust, scalable, and long-lasting industrial structures tailored to your industrial workflow for manufacturing plants and warehouses."
          backgroundImage={industrialStructure}
          primaryButtonText="Get Quote"
          primaryButtonLink="/enquiry"
          secondaryButtonText="Contact Us"
          secondaryButtonLink="/contact"
        />

        {/* Main Content */}
        <section className="section-padding">
          <div className="container-custom">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="animate-slide-in-left order-2 lg:order-1">
                <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-foreground mb-6">
                  Strength Meets <span className="text-secondary">Engineering Excellence</span>
                </h2>
                <p className="text-muted-foreground mb-4">
                  Our industrial structures are built using top-quality materials and engineering standards. We focus on creating frameworks that support heavy loads, equipment installation, movement of goods, and continuous industrial activity.
                </p>
                <p className="text-muted-foreground mb-6">
                  Every structure is planned with precise load balancing, stability, and safety in mind.
                </p>

                <h3 className="font-montserrat text-xl font-bold text-foreground mb-4">
                  Applications:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {applications.map((app) => (
                    <div key={app} className="flex items-center gap-2 text-muted-foreground">
                      <svg
                        className="w-5 h-5 text-secondary flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M5 13l4 4L19 7"
                        />
                      </svg>
                      {app}
                    </div>
                  ))}
                </div>
              </div>

              <div className="animate-slide-in-right order-1 lg:order-2">
                <img
                  src={industrialStructure}
                  alt="Industrial Structure"
                  className="rounded-xl shadow-2xl w-full object-cover h-full"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section className="section-padding bg-muted">
          <div className="container-custom">
            <div className="text-center mb-12">
              <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-foreground mb-4">
                What Makes Our Structures <span className="text-primary">Reliable?</span>
              </h2>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {features.map((feature, index) => (
                <div
                  key={feature.title}
                  className={`p-6 bg-card rounded-xl shadow-lg card-hover animate-slide-up stagger-${index + 1}`}
                  style={{ animationFillMode: "both" }}
                >
                  <div className="w-12 h-12 bg-secondary/10 rounded-lg flex items-center justify-center mb-4">
                    <svg
                      className="w-6 h-6 text-secondary"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  </div>
                  <h3 className="font-montserrat text-xl font-bold text-foreground mb-2">
                    {feature.title}
                  </h3>
                  <p className="text-muted-foreground">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="section-padding bg-primary">
          <div className="container-custom text-center">
            <h2 className="font-montserrat text-3xl md:text-4xl font-bold text-primary-foreground mb-4">
              Your Industrial Infrastructure Partner
            </h2>
            <p className="text-primary-foreground/90 text-lg mb-8 max-w-2xl mx-auto">
              At TR Structures, we don't just build frames — we build complete industrial environments with expertise and professional installation.
            </p>
            <Link
              to="/enquiry"
              className="btn-secondary inline-block px-8 py-4 text-lg font-medium hover:bg-secondary/90 transition"
            >
              Start Your Project
            </Link>
          </div>
        </section>
      </main>
    </Layout>
  );
};

export default IndustrialStructures;